import React, { useState, useContext } from "react";

import {
  Card,
  DisplayText,
  FormLayout,
  Icon,
  Link,
  Modal,
  ResourceItem,
  ResourceList,
  Stack,
  TextContainer,
  TextField,
  TextStyle,
  Thumbnail,
} from "@shopify/polaris";
import { CircleTickMajor, DeleteMinor, DragHandleMinor, EditMinor } from "@shopify/polaris-icons";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useMutation } from "react-apollo";
import { PrivateContext } from "lib/context";

import constant from "lib/constant/constant";
import baseHelper from "lib/helpers/base";

// import component
import { Toast, SkeletonCard } from "lib/components";

import { imageHelper, errorHelper } from "lib/helpers";
import Popover from "lib/components/popover/popover";

// import queries
import { GET_PRODUCT } from "app/product/apollo/queries";
import {
  BULK_UPDATE_VARIANT,
  DELETE_VARIANT,
  REORDER_PRODUCT_VARIANTS,
  UPDATE_VARIANT_IMAGE,
} from "app/product/apollo/mutations";

const RenderVariants = (props) => {
  const { history, currentUser, match, cms } = useContext(PrivateContext);
  const { data, setBanner, setToastMessage, productLoading, handleTabChange, setDisabledButton } = props || {};
  const { _id: productItemId, images } = data;
  const { moneyFormat } = currentUser;
  const isStoreConnected = baseHelper.isVendorStoreConnected(currentUser);
  const [message, setMessage] = useState("");
  const [updateQuantity, setQuantity] = useState("");
  const [updatePrice, setUpdatePrice] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [active, setActive] = useState(false);
  const [openQuantity, setOpenQuantity] = useState(false);
  const [deleteActive, setDeleteActive] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [activePopover, setActivePopover] = useState({});
  const [addImageModal, setAddImageModal] = useState({});
  const [selectedImage, setSelectedImage] = useState({});
  const [submitDisable, setSubmitDisable] = useState(false);
  const [reorderLoadingPosition, setReorderLoadingPosition] = useState(null);
  const {
    value: { MIN_PRICE = 0, MAX_FLAT = 99999999 },
  } = constant;
  const reorderSuccessMessage = "Variant order updated successfully.";

  const { gql, symbol } = constant;

  // GQL calls
  const [updateMarkupVariant, { loading: loadingBulkUpdate }] = useMutation(BULK_UPDATE_VARIANT, {
    refetchQueries: [
      {
        query: GET_PRODUCT,
        variables: { input: { id: match.params.id } },
      },
    ],
  });
  const [deleteVariant, { loading: loadingDeleteVariant }] = useMutation(DELETE_VARIANT, {
    refetchQueries: [
      {
        query: GET_PRODUCT,
        variables: { input: { id: match.params.id } },
      },
    ],
  });

  const [updateVariant, { loading: loadingUpdateVariant }] = useMutation(UPDATE_VARIANT_IMAGE, {
    refetchQueries: [
      {
        query: GET_PRODUCT,
        variables: { input: { id: match.params.id } },
      },
    ],
  });
  const [reorderProductVariants] = useMutation(REORDER_PRODUCT_VARIANTS, {
    refetchQueries: [
      {
        query: GET_PRODUCT,
        variables: { input: { id: match.params.id } },
      },
    ],
  });

  const acceptOnlyValidInput = (value, prevValue, max = MAX_FLAT, min = MIN_PRICE) => {
    if (value >= min && value <= max) {
      return (baseHelper.validatePositiveNumericValues(value) && value) || (value !== "" && prevValue) || "";
    }
    if (!value) {
      return "";
    }
    return prevValue;
  };

  const handleEditMarkup = () => {
    setActive(!active);
    setMessage("");
  };
  const handleEditQuantity = () => {
    setOpenQuantity(!openQuantity);
    setMessage("");
  };

  const handleDeleteModal = (index) => {
    setSelectedId(index);
    setDeleteActive(!deleteActive);
  };
  const bulkActions = [
    { content: cms("label.updatePrice"), onAction: () => handleEditMarkup() },
    { content: cms("label.updateQuantity"), onAction: () => handleEditQuantity() },
  ];

  if (productLoading) {
    return <SkeletonCard />;
  }

  const handleUpdateMarkup = (fieldName = false) => {
    const value = {
      productId: productItemId,
      positions: selectedItems,
    };

    if (fieldName === constant.PRICE) {
      value.price = parseFloat(updatePrice) || parseInt(0, 10);
    }
    if (fieldName === constant.INVENTORY_QUANTITY) {
      value.inventoryQuantity = updateQuantity || parseInt(0, 10);
    }
    updateMarkupVariant({ variables: { input: value } })
      // eslint-disable-next-line consistent-return
      .then((res) => {
        setSelectedItems([]);
        const responseError = baseHelper.getResponseError(res.data, gql.BULK_UPDATE_VARIANT);
        const successMessage =
          fieldName === constant.INVENTORY_QUANTITY ? cms("message.success.quantity") : cms("message.success.price");
        setDisabledButton(false);
        if (responseError) {
          setOpenQuantity(false);
          setActive(false);
          return setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        }
        setOpenQuantity(false);
        setActive(false);
        setToastMessage(successMessage);
      })
      .catch((exception) => {
        setOpenQuantity(false);
        setActive(false);
        setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
      });
    setSubmitDisable(false);
  };
  const handleDeleteVariant = () => {
    setToastMessage("");
    const value = {
      id: selectedId + 1,
      productId: productItemId,
    };
    deleteVariant({ variables: { input: value } })
      .then((res) => {
        const responseError = baseHelper.getResponseError(res.data, gql.DELETE_VARIANT);
        if (responseError) {
          handleDeleteModal();
          setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
        }
        const responseData = baseHelper.getResponseData(res.data, gql.DELETE_VARIANT);
        if (responseData) {
          setToastMessage(cms("message.success.variantDelete"));
          handleDeleteModal();
          setTimeout(() => {
            setSelectedId(null);
            handleTabChange(2);
            setDisabledButton(false);
          }, 2000);
        }
      })
      .catch((exception) => {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
        handleDeleteModal();
      });
  };
  const handleReorderVariants = (fromIndex, toIndex) => {
    const variants = (data && data.variants) || [];
    if (fromIndex === toIndex || toIndex < 0 || toIndex >= variants.length) {
      return;
    }
    const currentVariant = variants[fromIndex] || {};
    setToastMessage("");
    setMessage("");
    setReorderLoadingPosition(currentVariant.position || null);

    const orderedPositions = variants.map((variant) => variant.position);
    const movedPosition = orderedPositions.splice(fromIndex, 1)[0];
    orderedPositions.splice(toIndex, 0, movedPosition);

    reorderProductVariants({
      variables: {
        input: {
          productId: productItemId,
          positions: orderedPositions,
        },
      },
    })
      .then((res) => {
        const response = (res && res.data && res.data.reorderProductVariants) || {};
        if (response.error) {
          setBanner({ isOpen: true, status: constant.CRITICAL, title: response.error });
          return;
        }

        setSelectedItems([]);
        setBanner({
          isOpen: true,
          status: constant.SUCCESS,
          title: reorderSuccessMessage,
        });
        setMessage(reorderSuccessMessage);
        setToastMessage(reorderSuccessMessage);
        setDisabledButton(false);
      })
      .catch((exception) => {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
      })
      .finally(() => {
        setReorderLoadingPosition(null);
      });
  };
  const renderDeleteModal = () => {
    return (
      <Modal
        open={deleteActive}
        onClose={handleDeleteModal}
        title={cms("label.deleteVariant")}
        primaryAction={{
          content: cms("button.delete"),
          onAction: handleDeleteVariant,
          loading: loadingDeleteVariant,
          disabled: loadingDeleteVariant,
          destructive: true,
        }}
      >
        <Modal.Section>{cms("label.content")}</Modal.Section>
      </Modal>
    );
  };
  const renderModal = () => {
    return (
      <Modal
        open={active}
        onClose={handleEditMarkup}
        title={cms("label.updateVariantPrice")}
        primaryAction={{
          content: cms("label.updatePrice"),
          onAction: () => handleUpdateMarkup(constant.PRICE),
          loading: loadingBulkUpdate,
          disabled: !submitDisable || loadingBulkUpdate,
        }}
      >
        <Modal.Section>
          <TextField
            label={cms("label.price")}
            prefix={moneyFormat || symbol.DOLLAR}
            value={updatePrice && updatePrice.toString()}
            placeholder={constant.ZERO_PRICE}
            min={0}
            onChange={(val) => {
              setUpdatePrice(acceptOnlyValidInput(val, updatePrice));
              setSubmitDisable(val);
            }}
          />
        </Modal.Section>
      </Modal>
    );
  };
  const renderQuantityModal = () => {
    return (
      <Modal
        open={openQuantity}
        onClose={handleEditQuantity}
        title={cms("label.update")}
        primaryAction={{
          content: cms("label.updateQuantity"),
          onAction: () => handleUpdateMarkup(constant.INVENTORY_QUANTITY),
          loading: loadingBulkUpdate,
          disabled: !submitDisable || loadingBulkUpdate,
        }}
      >
        <Modal.Section>
          <TextField
            label={cms("label.quantity")}
            value={updateQuantity && updateQuantity.toString()}
            placeholder={constant.ZERO_PRICE}
            maxLength={10}
            min={0}
            onChange={(value) => {
              setQuantity(parseInt(value, 10));
              setSubmitDisable(value);
            }}
          />
        </Modal.Section>
      </Modal>
    );
  };

  const closeModal = () => {
    setAddImageModal({});
    setSelectedImage({});
  };

  const updateImage = () => {
    setToastMessage("");
    const value = {
      id: addImageModal.variantId,
      image: selectedImage.imageUrl,
      productId: match.params.id,
    };
    updateVariant({ variables: { input: value } })
      .then((res) => {
        const responseError = baseHelper.getResponseError(res.data, gql.UPDATE_VARIANT_IMAGE);
        if (responseError) {
          setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
          closeModal();
        }
        const responseData = baseHelper.getResponseData(res.data, gql.UPDATE_VARIANT_IMAGE);
        if (responseData) {
          setToastMessage(cms("message.success.variantImage"));
          closeModal();
        }
      })
      .catch((exception) => {
        setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
        closeModal();
      });
  };

  const onImageChange = (nImage) => {
    setSelectedImage(selectedImage.imageUrl === nImage.imageUrl ? {} : nImage);
  };

  const renderSelectImage = () => {
    const allImages = (images && images.filter((item) => item && item.imageUrl)) || [];
    const productImages = allImages.map((nImage) => {
      const isSelectedImage = nImage.imageUrl === selectedImage.imageUrl;
      return (
        <Stack.Item key={nImage.position}>
          <Stack alignment="center" vertical>
            <Stack.Item>
              <Link onClick={() => onImageChange(nImage)}>
                <Thumbnail
                  source={imageHelper.resize({ url: nImage.imageUrl, type: constant.imageTypes.THUMBNAIL })}
                  size="large"
                />
              </Link>
            </Stack.Item>
            {isSelectedImage && (
              <Stack.Item>
                <Icon source={CircleTickMajor} color="greenDark" backdrop />
              </Stack.Item>
            )}
          </Stack>
        </Stack.Item>
      );
    });
    return <Stack>{productImages}</Stack>;
  };

  const renderImageModal = () => {
    return (
      <Modal
        open={addImageModal.isOpen}
        onClose={() => closeModal()}
        title={cms("modal.title")}
        primaryAction={{
          content: cms("modal.button"),
          onAction: () => updateImage(),
          disabled: !(selectedImage && selectedImage.imageUrl),
          loading: loadingUpdateVariant,
        }}
      >
        <Modal.Section>
          <Card sectioned>{renderSelectImage()}</Card>
        </Modal.Section>
      </Modal>
    );
  };

  const handleDragEnd = (result) => {
    if (!result.destination || result.source.index === result.destination.index) return;
    handleReorderVariants(result.source.index, result.destination.index);
  };

  const renderItem = (item, _, index) => {
    const {
      image,
      option1Val = false,
      option2Val = false,
      option3Val = false,
      option1 = cms("label.size"),
      option2 = cms("label.color"),
      option3 = cms("label.material"),
      inventoryQuantity,
      price,
      sku,
      position,
    } = item;
    const media = (
      <Link onClick={() => setAddImageModal({ isOpen: true, variantId: position })}>
        <Thumbnail
          source={imageHelper.resize({ url: image, type: constant.imageTypes.THUMBNAIL }) || constant.NOIMAGESNAP}
          alt={`image of variant ${index}`}
        />
      </Link>
    );

    return (
      <Draggable key={String(position)} draggableId={String(position)} index={index} isDragDisabled={isStoreConnected}>
        {(provided) => (
          <div ref={provided.innerRef} {...provided.draggableProps}>
            <ResourceItem id={index + 1} media={media}>
              <TextContainer>
                <Stack wrap={false}>
                  {!isStoreConnected && (
                    <Stack.Item>
                      <span
                        {...provided.dragHandleProps}
                        style={{ cursor: "grab", display: "flex", alignItems: "center" }}
                      >
                        <Icon source={DragHandleMinor} />
                      </span>
                    </Stack.Item>
                  )}
                  <Stack.Item fill>
                    {option1Val && <div className="variantEllipsis">{`${option1}: ${option1Val}`}</div>}
                    {option2Val && <div className="variantEllipsis">{`${option2}: ${option2Val}`}</div>}
                    {option3Val && <div className="variantEllipsis">{`${option3}: ${option3Val}`}</div>}
                  </Stack.Item>
                  <Stack.Item>
                    <Popover
                      active={activePopover[index]}
                      setActive={() => setActivePopover({ [index]: !activePopover[index] })}
                      loading={reorderLoadingPosition === position}
                      options={[
                        {
                          content: cms("button.edit"),
                          icon: EditMinor,
                          onAction: () => history.push(`/variants/edit/${productItemId}/${index + 1}`),
                        },
                        {
                          content: cms("button.delete"),
                          icon: DeleteMinor,
                          destructive: true,
                          onAction: () => handleDeleteModal(index),
                        },
                      ]}
                    />
                  </Stack.Item>
                </Stack>
                <FormLayout>
                  <FormLayout.Group condensed>
                    <TextField
                      label={cms("label.inventory")}
                      value={(inventoryQuantity && inventoryQuantity.toString()) || ""}
                      disabled
                    />
                    <TextField
                      label={cms("label.price")}
                      value={(price ? price.toString() : "0") || ""}
                      prefix={moneyFormat}
                      disabled
                    />
                    <TextField label={cms("label.sku")} value={(sku && sku.toString()) || ""} disabled />
                  </FormLayout.Group>
                </FormLayout>
              </TextContainer>
            </ResourceItem>
          </div>
        )}
      </Draggable>
    );
  };

  const view = (dataNew) => {
    const { variants } = dataNew;
    return (
      <div>
        {!(variants && variants.length) && (
          <>
            <TextContainer>
              <DisplayText size="extraSmall">{cms("section.variant.content")}</DisplayText>
              <br />
              <DisplayText size="extraSmall">
                <TextStyle variation="strong">{`${cms("label.please")}: `}</TextStyle>
                {cms("label.caption.add")}
              </DisplayText>
            </TextContainer>
          </>
        )}
        {variants && variants.length > 0 && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="variants">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  <ResourceList
                    items={variants}
                    renderItem={renderItem}
                    selectedItems={selectedItems}
                    onSelectionChange={setSelectedItems}
                    bulkActions={bulkActions}
                    idForItem={(_, index) => index + 1}
                  />
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
        <div className="toast">
          <Toast message={message} setToast={setMessage} />
        </div>
      </div>
    );
  };
  return [renderImageModal(), renderModal(), renderQuantityModal(), renderDeleteModal(), view(data)];
};

export default RenderVariants;
